/**
 * Module: ProfileScreen
 * Role: Buyer profile — /api/v2/buyer/profile + /profile/stats. Avatar, stats strip,
 *       account InsetList (Реквизиты, Находки, Статистика, Помощь, Оферта), creator row.
 * Uses: IOSScreen, PageHeader, Card, SectionHeader, ListRow, EmptyState, Skeleton, Ic
 * Used by: AppShell (routed at /profile)
 * Glossary: ai/glossary/buyer.md
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import {
  IOSScreen,
  PageHeader,
  Card,
  SectionHeader,
  ListRow,
  EmptyState,
  Skeleton,
  Ic,
} from '../components/atoms';
import type { ApiPlatform } from '../platform/types';
import { P, S, T } from '../styles/tokens';

// ─── Backend contracts (planner-verified, field names exact) ─────────────────
// L-240: inline interfaces — never import from api/types.ts (stale placeholder).
// ApiPlatform imported directly from platform/types (SSOT per ADR-025) — avoids
// raw 'tg'/'vk'/'max' literals which trigger ADR-023 ESLint rule.

// GET /api/v2/buyer/profile — verified against profile.py:40-56
// FTR-1106 Task 4: added creator fields (account_kind, creator_status,
// creator_verified_at, creator_profile_complete) — additive, optional,
// backward-compat (old backend returns undefined → defaults apply).
interface ProfileView {
  id: string;
  platform: ApiPlatform;
  nickname: string | null;
  avatar_url: string | null;
  trust_score: number; // 0.0-1.0
  sbp_phone_masked: string | null;
  sbp_complete: boolean; // TECH-1004: added server-side profile.py:50
  notify_daily_campaigns: boolean;
  linked_platforms_count: number;
  created_at: string; // ISO datetime
  // FTR-1106 Task 4 — creator role fields (optional for backward-compat)
  account_kind?: 'standard' | 'creator';
  creator_status?: string | null;
  creator_profile_complete?: boolean;
}

// GET /api/v2/buyer/profile/stats — TECH-1010 Task 3
// SSOT: src/api/v2/buyer/schemas.py:173-192 ProfileStatsView
// "miniapp computes any displayed percentage from these counts" — server comment.
interface ProfileStatsView {
  completed_slots_count: number;
  approved_count: number;
  rejected_count: number;
  sla_cancelled_count: number;
}

// ─── Date → RU month+year helper ─────────────────────────────────────────────
// Formats ISO datetime as "с мая 2026" (prepend "с " at call site).
function formatJoinDate(isoDatetime: string): string {
  const d = new Date(isoDatetime);
  const month = d.toLocaleString('ru-RU', { month: 'long' }); // "май", "декабрь"…
  const year = d.getFullYear();
  return `с ${month} ${year}`;
}

// ─── Avatar initials fallback ─────────────────────────────────────────────────
// Returns the first letter of nickname (uppercased), or the platform initial.
function getAvatarInitial(nickname: string | null, platform: string): string {
  if (nickname && nickname.length > 0) {
    return nickname[0].toUpperCase();
  }
  return platform[0].toUpperCase();
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function ProfileSkeleton() {
  return (
    <div style={{ padding: '8px 16px 0' }}>
      {/* Avatar card skeleton */}
      <Card p={18} r={20}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Skeleton variant="circle" w={60} h={60} />
          <div style={{ flex: 1 }}>
            <Skeleton h={18} w={140} style={{ marginBottom: 8 }} />
            <Skeleton h={13} w={100} />
          </div>
        </div>
      </Card>
      {/* Stats strip skeleton */}
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ flex: 1 }}>
            <Card p={14} r={20}>
              <Skeleton h={20} w={32} style={{ marginBottom: 6 }} />
              <Skeleton h={12} w={52} />
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Account list items ─────────────────────────────────────────────────────────
// All 5 rows wired (FTR-1002/1005/1033). Titles match test exact-text assertions.
const ACCOUNT_ROWS: Array<{
  title: string;
  iconName: 'card' | 'tag' | 'chart' | 'question' | 'doc';
  iconBg: string;
  navigateTo?: string; // every row wired post-FTR-1033
}> = [
  { title: 'Реквизиты',    iconName: 'card',     iconBg: P.terracotta, navigateTo: '/requisites' },
  { title: 'Находки',      iconName: 'tag',      iconBg: P.jade,       navigateTo: '/finds' },
  { title: 'Статистика',   iconName: 'chart',    iconBg: P.warn,       navigateTo: '/stats' },
  { title: 'Помощь',       iconName: 'question', iconBg: '#5E8FA3',    navigateTo: '/help' },
  { title: 'Оферта',       iconName: 'doc',      iconBg: P.muted,      navigateTo: '/oferta' },
];

// ─── Creator status row (FTR-1106 Task 4) ─ navigate() only, no openExternal ──

interface CreatorRowSpec {
  title: string;
  subtitle?: string;
  navigateTo?: string; // undefined = read-only (no tap, no chevron)
  muted?: boolean; // true = tertiaryLabel color for read-only state
}

function resolveCreatorRow(
  accountKind: 'standard' | 'creator' | undefined,
  creatorStatus: string | null | undefined,
  creatorProfileComplete: boolean | undefined,
): CreatorRowSpec {
  if (accountKind === 'creator' && creatorStatus === 'verified') {
    if (creatorProfileComplete === false) {
      return { title: 'Профиль креатора', subtitle: 'Заполни профиль', navigateTo: '/creator-onboarding/profile' };
    }
    return { title: 'Креатор', subtitle: 'Роль подтверждена', muted: true };
  }
  if (creatorStatus === 'pending' || creatorStatus === 'pending_scraping') {
    return { title: 'Заявка на модерации', subtitle: 'Обычно 1-2 рабочих дня', navigateTo: '/creator-status' };
  }
  if (creatorStatus === 'rejected') {
    return { title: 'Подать заявку снова', navigateTo: '/creator-onboarding/promo' };
  }
  return { title: 'Стать креатором', subtitle: 'Премиум-задания с высоким кешбэком', navigateTo: '/creator-onboarding/promo' };
}

interface CreatorStatusRowProps {
  profile: ProfileView;
  navigate: (path: string) => void;
  isLastInGroup: boolean;
}

function CreatorStatusRow({ profile, navigate, isLastInGroup }: CreatorStatusRowProps) {
  const spec = resolveCreatorRow(
    profile.account_kind,
    profile.creator_status,
    profile.creator_profile_complete,
  );
  const isTappable = spec.navigateTo !== undefined;
  return (
    <ListRow
      icon={<Ic name="flame" size={18} color="#ffffff" />}
      iconBg={P.terracotta}
      title={spec.title}
      subtitle={spec.subtitle}
      isLast={isLastInGroup}
      style={spec.muted ? { opacity: 0.7 } : undefined}
      onClick={isTappable ? () => navigate(spec.navigateTo!) : undefined}
      right={isTappable ? <Ic name="chevR" size={16} color={S.tertiaryLabel} /> : undefined}
    />
  );
}

// ─── ProfileScreen ────────────────────────────────────────────────────────────

export default function ProfileScreen() {
  const navigate = useNavigate();

  // Profile query
  const {
    data: profile,
    isPending: profilePending,
    isError: profileError,
  } = useQuery<ProfileView>({
    queryKey: ['buyer', 'profile'],
    queryFn: () => apiFetch<ProfileView>('/api/v2/buyer/profile'),
    retry: false,
  });

  // Stats query — TECH-1010/FTR-1032; independent key per IA-1.
  const {
    data: stats,
    isError: statsError,
  } = useQuery<ProfileStatsView>({
    queryKey: ['buyer', 'profile', 'stats'],
    queryFn: () => apiFetch<ProfileStatsView>('/api/v2/buyer/profile/stats'),
    retry: false,
  });

  // Derived stat-card values — "—" fallback on error (DA-2, DA-3).
  const statsAvailable = stats !== undefined && !statsError;
  const tasksDisplay = statsAvailable ? String(stats.completed_slots_count) : '—';
  const approvedDisplay = statsAvailable ? String(stats.approved_count) : '—';
  let acceptanceDisplay = '—';
  if (statsAvailable) {
    const denom = stats.approved_count + stats.rejected_count + stats.sla_cancelled_count;
    if (denom > 0) {
      acceptanceDisplay = `${Math.round((stats.approved_count / denom) * 100)}%`;
    }
  }

  return (
    <IOSScreen tabBarSpace>
      <PageHeader title="Я" />

      {/* ── Loading state ── */}
      {profilePending && <ProfileSkeleton />}

      {/* EC-10: profile error → honest EmptyState, no fabricated data */}
      {profileError && !profilePending && (
        <EmptyState
          title="Профиль недоступен"
          subtitle="Попробуй позже"
        />
      )}

      {/* ── Success state ── */}
      {!profilePending && !profileError && profile !== undefined && (
        <div style={{ padding: '8px 16px 0' }}>
          {/* Avatar Card */}
          <Card p={18} r={20}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: P.terracotta,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: '#ffffff',
                    font: T.title2,
                    letterSpacing: -0.2,
                  }}
                >
                  {getAvatarInitial(profile.nickname, profile.platform)}
                </div>
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* EC-10: nickname, not display_name */}
                <p
                  style={{
                    font: T.headline,
                    color: S.label,
                    margin: '0 0 2px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {profile.nickname ?? 'Без имени'}
                </p>
                <p
                  style={{
                    font: T.footnote,
                    color: S.secondaryLabel,
                    margin: 0,
                  }}
                >
                  {formatJoinDate(profile.created_at)}
                </p>
              </div>

              {/* Caveat star (prototype rot=-4, jade, size=22). SVG star — no unicode glyph. */}
              {profile.trust_score > 0 && (
                <span style={{ fontFamily: T.fontAccent, fontSize: 22, fontWeight: 700,
                  color: P.jade, transform: 'rotate(-4deg)', display: 'inline-flex',
                  alignItems: 'center', gap: 3, lineHeight: 1, flexShrink: 0 }}>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill={P.jade}>
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  {Math.round(profile.trust_score * 5 * 10) / 10}
                </span>
              )}
            </div>
          </Card>

          {/* Stats strip — 3 equal cards; prototype: p=14 r=20, center. Label first. */}
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            {/* Card 1: Заданий */}
            <div style={{ flex: 1 }}>
              <Card p={14} r={20}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ font: T.captionAlt, fontWeight: 600, color: S.secondaryLabel, margin: '0 0 4px' }}>
                    Заданий
                  </p>
                  <p style={{ font: T.title3, color: S.label, margin: 0 }}>
                    {tasksDisplay}
                  </p>
                </div>
              </Card>
            </div>

            {/* Card 2: Принято % (acceptance rate) */}
            <div style={{ flex: 1 }}>
              <Card p={14} r={20}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ font: T.captionAlt, fontWeight: 600, color: S.secondaryLabel, margin: '0 0 4px' }}>
                    Принято %
                  </p>
                  <p style={{ font: T.title3, color: P.success, margin: 0 }}>
                    {acceptanceDisplay}
                  </p>
                </div>
              </Card>
            </div>

            {/* Card 3: Принято (approved raw count) */}
            <div style={{ flex: 1 }}>
              <Card p={14} r={20}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ font: T.captionAlt, fontWeight: 600, color: S.secondaryLabel, margin: '0 0 4px' }}>
                    Принято
                  </p>
                  <p style={{ font: T.title3, color: P.terraDark, margin: 0 }}>
                    {approvedDisplay}
                  </p>
                </div>
              </Card>
            </div>
          </div>

          {/* Аккаунт section — InsetList (r=20, white bg, overflow:hidden) */}
          <SectionHeader>Аккаунт</SectionHeader>
          <div style={{
            background: S.secondaryGroupedBg,
            borderRadius: 20,
            overflow: 'hidden',
            border: `1px solid ${P.line}`,
            boxShadow: '0 1px 3px rgba(45,37,24,0.04)',
          }}>
            {/* Creator role row — always shown (changes label by status) */}
            <CreatorStatusRow
              profile={profile}
              navigate={navigate}
              isLastInGroup={false}
            />
            {ACCOUNT_ROWS.map((row, idx) => (
              <ListRow
                key={row.title}
                icon={<Ic name={row.iconName} size={18} color="#ffffff" />}
                iconBg={row.iconBg}
                title={row.title}
                isLast={idx === ACCOUNT_ROWS.length - 1}
                onClick={row.navigateTo ? () => navigate(row.navigateTo!) : undefined}
                right={row.navigateTo ? <Ic name="chevR" size={16} color={S.tertiaryLabel} /> : undefined}
              />
            ))}
          </div>

          {/* Version footer — prototype: caption2, tertiaryLabel, centered */}
          <div style={{
            marginTop: 22,
            textAlign: 'center',
            font: T.captionAlt,
            color: S.tertiaryLabel,
            lineHeight: 1.5,
            paddingBottom: 8,
          }}>
            Awardy · v1.0
          </div>
        </div>
      )}
    </IOSScreen>
  );
}
